import { Version, initApp } from '@one/api'
import { AuthService } from '@one/core'

export default ({ app, store }, inject) => {
  let baseURL = app.$config.BASE_URL
  const baseSsrURL = app.$config.BASE_SSR_URL
  if (baseURL && baseURL.endsWith('v1')) {
    // Removing version from baseURL
    baseURL = baseURL.substr(0, baseURL.length - 3)
  }
  // This variable is only as a safe-checker -> use case is exceptional
  let isRefreshing = false
  let failedQueue = []
  const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error)
      } else {
        prom.resolve(token)
      }
    })
    failedQueue = []
  }
  const apiService = initApp({
    baseURL: (process.server && baseSsrURL ? baseSsrURL : baseURL) || 'http://localhost:8080',
    version: Version.V1,
    debug: false,
    timing: app.$config.TIMING_ENABLED,
    cacheEnabled: process.client,
    queueEnabled: process.client,
    getToken: () => store.state.auth.accessToken,
    isTokenValid: (token) => {
      return AuthService.isAppTokenValid(token)
    },
    triggerTokenRefresh: async () => {
      if (app.$auth && !isRefreshing) {
        isRefreshing = true
        try {
          const isRefreshed = await app.$auth.tryToRefreshAppAccessToken()
          if (isRefreshed) {
            apiService.flushQueue()
          }
        } catch (e) {
          console.warn('Error triggerTokenRefresh')
        } finally {
          isRefreshing = false
        }
      }
    },
    async onResponseError(error) {
      const response = error.response
      const currentDate = new Date().valueOf()
      const logInformation = {
        ssr: typeof window === 'undefined', // quick check if in node
        service: 'oneAPI config',
        method: response?.config?.method,
        requestId: response?.data?.requestId,
        status: response?.status,
        tenantKey: response?.config?.headers['one-tenant'],
        timestamp: currentDate,
        url: response?.config?.url,
        params: response?.config?.params,
        msg: response?.data?.message,
        errorCode: response?.data?.errorCode,
      }
      app.$logger.warn(logInformation)

      const excludedUrls = ['/session/login', '/session/token']
      const originalRequest = error.config

      if (
        error.response.status === 401 &&
        !originalRequest._retry &&
        !excludedUrls.some(url => originalRequest.url.includes(url))
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then(() => {
              return this.resendRequest(originalRequest)
            })
            .catch((err) => {
              return Promise.reject(err)
            })
        }

        originalRequest._retry = true
        isRefreshing = true

        try {
          await app.$auth.tryToRefreshAccessToken()
          processQueue(null)
          return this.resendRequest(originalRequest)
        } catch (_error) {
          processQueue(_error, null)
          await app.$auth.logout()
          await app.router.push('/login')
          return Promise.reject(_error)
        } finally {
          isRefreshing = false
        }
      }

      return Promise.reject(error)
    },
    getTenantKey: () => app.$tenantConfig.tenantKey,
  })
  inject('api', apiService)
}
